import type { BoardTile, InitialDeal } from "./types";

export type OnboardingState = {
  hasSeenTutorial: boolean;
  hasSeenFlowExplanation: boolean;
  hasSeenNegativeExplanation: boolean;
  hasSeenIncrementExplanation: boolean;
  hasSeenRefillExplanation: boolean;
  hasCompletedFirstRun: boolean;
};

export type LossEducation = {
  headline: string;
  explanation: string;
  tip: string;
};

const STORAGE_KEY = "tavela.onboarding";
const DEFAULT_ONBOARDING_STATE: OnboardingState = {
  hasSeenTutorial: false,
  hasSeenFlowExplanation: false,
  hasSeenNegativeExplanation: false,
  hasSeenIncrementExplanation: false,
  hasSeenRefillExplanation: false,
  hasCompletedFirstRun: false
};

export const FIRST_RUN_INITIAL_DEAL: InitialDeal = {
  board: [2, 4, 6, 7, 9],
  hand: [2, 1, 4]
};

function getLocalStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizeOnboardingState(value: unknown): OnboardingState {
  if (!value || typeof value !== "object") {
    return DEFAULT_ONBOARDING_STATE;
  }

  const record = value as Partial<OnboardingState>;

  return {
    hasSeenTutorial: record.hasSeenTutorial === true,
    hasSeenFlowExplanation: record.hasSeenFlowExplanation === true,
    hasSeenNegativeExplanation: record.hasSeenNegativeExplanation === true,
    hasSeenIncrementExplanation: record.hasSeenIncrementExplanation === true,
    hasSeenRefillExplanation: record.hasSeenRefillExplanation === true,
    hasCompletedFirstRun: record.hasCompletedFirstRun === true
  };
}

export function loadOnboardingState(): OnboardingState {
  const storage = getLocalStorage();
  if (storage === null) {
    return DEFAULT_ONBOARDING_STATE;
  }

  try {
    const storedValue = storage.getItem(STORAGE_KEY);
    if (storedValue === null) {
      return DEFAULT_ONBOARDING_STATE;
    }

    return normalizeOnboardingState(JSON.parse(storedValue));
  } catch {
    return DEFAULT_ONBOARDING_STATE;
  }
}

export function saveOnboardingState(value: OnboardingState): OnboardingState {
  const nextState = normalizeOnboardingState(value);
  const storage = getLocalStorage();
  if (storage === null) {
    return nextState;
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(nextState));
  } catch {
    // Ignore storage failures so onboarding can fall back to session behavior.
  }

  return nextState;
}

export function shouldUseGuidedRun(value: OnboardingState): boolean {
  return !value.hasCompletedFirstRun;
}

export function shouldShowRoundZeroTutorial(value: OnboardingState): boolean {
  return !value.hasSeenTutorial && !value.hasCompletedFirstRun;
}

const GENERIC_LOSS_EDUCATION: LossEducation = {
  headline: "No legal moves left",
  explanation: "You ran out of valid matches, 10-clears, and builds under 10.",
  tip: "Try clearing 8s and 9s earlier before they trap the board."
};

export function getLossEducation(board: readonly BoardTile[]): LossEducation {
  const loneTile = board.length === 1 ? board[0] : null;

  if (loneTile?.value === 9) {
    return {
      headline: "No legal moves left",
      explanation: "The last tile was a 9, and 9s can only clear.",
      tip: "Keep a 1 or another 9 available, or clear 9s before they get stranded."
    };
  }

  if (loneTile?.value === 8) {
    return {
      headline: "No legal moves left",
      explanation: "The last tile was an 8 and the hand had no safe partner left.",
      tip: "Use 8s sooner, before the board gets tight and your options disappear."
    };
  }

  if (board.some((tile) => tile.source === "built" && tile.value === 9)) {
    return {
      headline: "No legal moves left",
      explanation: "A built 9 boxed in the board at the end of the run.",
      tip: "Built 9s cannot build further, so cash them out before they become your last tile."
    };
  }

  if (board.some((tile) => tile.value >= 8)) {
    return {
      headline: "No legal moves left",
      explanation: "High tiles with very few partners were left on the board.",
      tip: "Clear 8s and 9s earlier, especially after you build into them."
    };
  }

  return GENERIC_LOSS_EDUCATION;
}
